import { formatCalendarDate } from '@/lib/utils/date';
import { DeleteDocumentButton } from './DeleteDocumentButton';
import { DocumentClassificationPanel } from '@/features/document-review/DocumentClassificationPanel';
import { ReportSigningPanel } from './ReportSigningPanel';
import { exportDocumentPdf } from '@/features/rendering';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { toast } from 'sonner';
import { useCallback, useState } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  Archive,
  ChartGantt,
  Copy,
  Download,
  ExternalLink,
  FilePlus,
  FileText,
  Loader2,
  Mic,
  MoreHorizontal,
  Printer,
  RefreshCw,
  Save,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { hasUsableTranscript } from '../utils';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { Separator } from '@/components/ui/separator';
import { EditorToolbar } from './editor/EditorToolbar';
import { DocumentTagPicker } from './DocumentTagPicker';
import { P, RequirePermission } from '@/features/permissions';
import { useDocumentEditor } from './editor/useDocumentEditor';
import { reportPlainText } from './editor/reportPlainText';
import { RegenerateDocumentDialog } from './RegenerateDocumentDialog';
import { useEncounter, useEncounterAudioSegments } from '../api/encounters-api';
import { type GeneratedDocumentOut, useDocumentSources, useUpdateDocument } from '../api/documents-api';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const statusColors: Record<string, string> = {
  exported: 'bg-info-surface text-info',
  draft: 'bg-warning-surface text-warning',
  archived: 'bg-muted text-muted-foreground',
  signed: 'bg-success-surface text-success',
  finalized: 'bg-success-surface text-success',
};

interface DocumentViewerSheetProps {
  document: GeneratedDocumentOut | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  /** Temporal dashboard deep link; non-null only for superusers. */
  temporal_url?: string | null;
}

export function DocumentViewerSheet({
  open,
  document: suppliedDocument,
  onOpenChange,
  temporal_url,
}: DocumentViewerSheetProps) {
  const { can } = usePermissions();
  const { data: freshDocument, refetch: refreshDocument } = useQuery<GeneratedDocumentOut>({
    queryKey: ['signing-document', suppliedDocument?.organization_id, suppliedDocument?.id],
    enabled: open && !!suppliedDocument,
    queryFn: async () =>
      (
        await clientApi.get(
          `/api/v1/organizations/${suppliedDocument!.organization_id}/documents/${suppliedDocument!.id}`,
        )
      ).data,
  });
  const document = freshDocument || suppliedDocument;
  const editable =
    !!document && !document.signed_version_id && document.status !== 'generating' && can('update_document');
  const { formatInstant } = useOrgDateFormat();
  const updateDocument = useUpdateDocument();
  const [isDirty, setIsDirty] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [regenerateDialog, setRegenerateDialog] = useState<{ open: boolean; variant: 'regenerate' | 'new' }>({
    open: false,
    variant: 'regenerate',
  });

  // Source encounter(s) → audio playback. Most generated documents are
  // produced from exactly one encounter (the typical Quick Start path).
  const { data: sources = [] } = useDocumentSources(open ? document?.id : undefined);
  const encounterId = sources[0]?.encounter_id;
  const { data: audioSegments = [] } = useEncounterAudioSegments(open ? encounterId : undefined);
  // Gate the regenerate / new-document actions on the first source encounter's
  // transcript (single-source assumption, per the note above). The backend
  // independently rejects generation when no source has a transcript, so the
  // rare multi-source case stays safe even if this gate is conservative.
  const { data: encounter, isLoading: isEncounterLoading } = useEncounter(open ? encounterId : undefined);
  const hasTranscript = hasUsableTranscript(encounter?.transcript);

  const handleUpdate = useCallback(() => {
    setIsDirty(true);
  }, []);

  const editor = useDocumentEditor({
    onUpdate: handleUpdate,
    initialContent: document?.content ?? '',
    editable,
  });

  if (!document) return null;

  const handleCopy = async (format: 'plain' | 'markdown') => {
    if (!editor) return;
    // Serialize the current editor, including unsaved edits. Parsing formatting
    // through the editor preserves literal clinical punctuation and hard breaks.
    const content = format === 'plain'
      ? reportPlainText(editor.state.doc)
      : editor.storage.markdown.getMarkdown();
    try {
      await navigator.clipboard.writeText(content);
      toast.success(format === 'plain' ? 'Report copied as plain text' : 'Report copied as Markdown');
    } catch {
      toast.error('Unable to copy report. Please check clipboard permissions and try again.');
    }
  };

  const handleSave = async () => {
    if (!editor || !editable) return;
    const content = editor.storage.markdown.getMarkdown();
    try {
      await updateDocument.mutateAsync({ data: { content }, documentId: document.id });
      setIsDirty(false);
      await refreshDocument();
      toast.success('Document saved');
    } catch {
      toast.error('Failed to save document');
    }
  };

  const handleExport = async (action: 'print' | 'download') => {
    if (exporting) return;
    setExporting(true);
    try {
      const base = `/api/v1/organizations/${document.organization_id}/documents/${document.id}`;
      await exportDocumentPdf(base, document.signed_version_id ?? null, action);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to prepare PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Hide the default `SheetPrimitive.Close` (top-4 right-4) — we render
        // our own X next to the dropdown menu in the header below.
        className="flex flex-col gap-3 w-full sm:max-w-4xl overflow-hidden p-4 sm:p-6 [&>[data-slot=sheet-default-close]]:hidden">
        <SheetHeader className="shrink-0 gap-1 p-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <SheetTitle>{document.title || document?.template?.name || 'Document'}</SheetTitle>
            </div>
            <div className="flex items-center gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {encounterId && (
                    <RequirePermission permission={P.GENERATE_DOCUMENT}>
                      <DropdownMenuItem
                        disabled={!hasTranscript}
                        onClick={() => setRegenerateDialog({ open: true, variant: 'regenerate' })}>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Regenerate
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!hasTranscript}
                        onClick={() => setRegenerateDialog({ open: true, variant: 'new' })}>
                        <FilePlus className="mr-2 h-4 w-4" />
                        New document from transcript
                      </DropdownMenuItem>
                      {!isEncounterLoading && !hasTranscript && (
                        <DropdownMenuLabel className="px-2 py-1 text-xs font-normal text-muted-foreground">
                          No transcript available to regenerate from.
                        </DropdownMenuLabel>
                      )}
                      <DropdownMenuSeparator />
                    </RequirePermission>
                  )}
                  <DropdownMenuGroup>
                    <DropdownMenuItem disabled={!editor} onClick={() => void handleCopy('plain')}>
                      <Copy />
                      Copy as plain text
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={!editor} onClick={() => void handleCopy('markdown')}>
                      <Copy />
                      Copy as Markdown
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  {can('export_document') && (
                    <>
                      <DropdownMenuItem
                        disabled={exporting || isDirty || document.status === 'generating'}
                        onClick={() => void handleExport('print')}>
                        <Printer className="mr-2 h-4 w-4" />
                        Print
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={exporting || isDirty || document.status === 'generating'}
                        onClick={() => void handleExport('download')}>
                        <Download className="mr-2 h-4 w-4" />
                        Export PDF
                      </DropdownMenuItem>
                    </>
                  )}
                  {document.status !== 'archived' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => toast.info('Archive feature coming soon')}>
                        <Archive className="mr-2 h-4 w-4" />
                        Archive
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DeleteDocumentButton document={document} menu onDeleted={() => onOpenChange(false)} />
                  {temporal_url && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Support</DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <a target="_blank" href={temporal_url} rel="noopener noreferrer">
                          <ChartGantt className="mr-2 h-4 w-4" />
                          View Temporal workflow
                          <ExternalLink className="ml-auto h-4 w-4" />
                        </a>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <SheetClose asChild>
                <Button size="icon" variant="ghost" aria-label="Close" className="h-8 w-8">
                  <X className="h-4 w-4" />
                </Button>
              </SheetClose>
            </div>
          </div>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className={`text-xs ${statusColors[document.status] ?? ''}`}>
              {document.status}
            </Badge>
            <span className="text-xs">
              {encounter?.encounter_date && <>Visit {formatCalendarDate(encounter.encounter_date, 'MMM d, yyyy')} &middot; </>}
              Generated {formatInstant(document.generated_at, "MMM d, yyyy 'at' h:mm a")}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div
          className="min-h-0 flex-1 overflow-y-scroll overscroll-contain flex flex-col gap-3 pr-2"
          style={{ scrollbarGutter: 'stable' }}>
          <ReportSigningPanel
            key={document.id}
            document={document}
            dirty={isDirty}
            onChanged={() => {
              setIsDirty(false);
              void refreshDocument();
            }}
          />
          {/* Source recordings */}
          {audioSegments.length > 0 && (
            <div className="rounded-lg border p-3 shrink-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Mic className="h-3.5 w-3.5" />
                Source Recordings ({audioSegments.length})
              </p>
              <div className="space-y-2">
                {audioSegments.map((seg, idx) => (
                  <div key={seg.id} className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Recording {idx + 1}
                      {seg.duration ? ` · ${formatDuration(seg.duration)}` : ''}
                    </p>
                    <audio controls preload="metadata" src={seg.audio_url} className="w-full h-8">
                      <track kind="captions" />
                    </audio>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generation Parameters — primitives only; nested objects are skipped
            (they are workflow-internal audit data like the report pipeline
            snapshot, not user-facing). */}
          {document.additional_context && hasPrimitiveEntries(document.additional_context) && (
            <div className="rounded-lg border p-3 shrink-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Generation Parameters
              </p>
              <div className="space-y-1">
                {Object.entries(document.additional_context)
                  .filter(([, v]) => isPrimitive(v))
                  .map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-3 text-xs">
                      <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium truncate">{formatPrimitive(value)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <Separator className="shrink-0" />

          {/* Editor area */}
          <div className="shrink-0 rounded-md border">
            {/* Sticky toolbar */}
            <div className="sticky top-0 z-10 bg-background border-b">
              {editable && <EditorToolbar editor={editor} />}
            </div>
            {/* Editor content */}
            <EditorContent editor={editor} />
          </div>
          {/* Tags */}
          <div className="rounded-lg border p-3 shrink-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Tags</p>
            <DocumentTagPicker kind="generated" documentId={document.id} />
            <DocumentClassificationPanel key={document.id} kind="generated" documentId={document.id} />
          </div>
        </div>
        {/* Save bar — only shown when dirty */}
        {isDirty && editable && (
          <div className="border-t pt-3 pb-1 shrink-0 flex items-center justify-end gap-2">
            <Button size="sm" disabled={updateDocument.isPending} onClick={handleSave}>
              {updateDocument.isPending ? (
                <>
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-3.5 w-3.5" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </SheetContent>
      {encounterId && (
        <RegenerateDocumentDialog
          encounterId={encounterId}
          open={regenerateDialog.open}
          variant={regenerateDialog.variant}
          patientId={document.patient_id ?? null}
          defaultTemplateId={document.template_id ?? null}
          onOpenChange={(next) => setRegenerateDialog((prev) => ({ ...prev, open: next }))}
        />
      )}
    </Sheet>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatPrimitive(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function hasPrimitiveEntries(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some(isPrimitive);
}

function isPrimitive(value: unknown): value is string | number | boolean {
  const t = typeof value;
  return t === 'string' || t === 'number' || t === 'boolean';
}
